import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, CheckCircle, Clock, Search } from 'lucide-react';
import Header from './components/Header4';
import Footer from './components/Footer4';
import Button from './components/Button';

const Orders = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    const orders = [
        {
            id: 'ORD-001',
            date: '2026-03-10',
            status: 'delivered',
            total: 12999,
            items: [
                { name: 'Banarasi Silk Saree', quantity: 1, image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=200' },
            ],
        },
        {
            id: 'ORD-002',
            date: '2026-03-15',
            status: 'shipped',
            total: 24500,
            items: [
                { name: 'Designer Lehenga Set', quantity: 1, image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=200' },
            ],
        },
        {
            id: 'ORD-003',
            date: '2026-03-18',
            status: 'processing',
            total: 3499,
            items: [
                { name: 'Cotton Kurta Collection', quantity: 1, image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=200' },
            ],
        },
    ];

    const statusConfig = {
        delivered: { label: 'Delivered', icon: CheckCircle, color: '#62B6CB', bg: '#F0F7FA' },
        shipped: { label: 'Shipped', icon: Truck, color: '#1B4965', bg: '#F0F7FA' },
        processing: { label: 'Processing', icon: Clock, color: '#6B7280', bg: '#F9FAFB' },
    };

    const filteredOrders = orders.filter((order) => {
        const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    return (
        <div className="w-full min-h-screen bg-[#F0F7FA]" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            <Header />
            <link href="https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            <main className="pt-20 md:pt-24 pb-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="text-2xl md:text-3xl font-semibold text-[#1B4965] mb-8 pt-4">My Orders</h1>

                    {/* Filters */}
                    <div className="flex flex-col sm:flex-row gap-3 mb-6">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
                            <input
                                type="text"
                                placeholder="Search by order ID..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 text-sm border border-[#E5E7EB] rounded-sm focus:outline-none focus:border-[#1B4965] bg-white text-[#1A1A1A]"
                            />
                        </div>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="px-4 py-2.5 text-sm border border-[#E5E7EB] rounded-sm focus:outline-none focus:border-[#1B4965] bg-white text-[#1A1A1A]"
                        >
                            <option value="all">All Orders</option>
                            <option value="processing">Processing</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                        </select>
                    </div>

                    {/* Orders List */}
                    {filteredOrders.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-sm border border-[#E5E7EB]">
                            <Package className="w-12 h-12 text-[#6B7280] mx-auto mb-4" />
                            <p className="text-lg text-[#6B7280] mb-4">No orders found</p>
                            <Button variant="primary" onClick={() => navigate('/template4/shop')}>
                                Start Shopping
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredOrders.map((order) => {
                                const status = statusConfig[order.status];
                                const StatusIcon = status.icon;
                                return (
                                    <div key={order.id} className="bg-white border border-[#E5E7EB] rounded-sm p-4 sm:p-6">
                                        {/* Header */}
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                                            <div>
                                                <p className="text-sm font-semibold text-[#1B4965]">{order.id}</p>
                                                <p className="text-xs text-[#6B7280]">Placed on {new Date(order.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                                            </div>
                                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm" style={{ backgroundColor: status.bg }}>
                                                <StatusIcon className="w-4 h-4" style={{ color: status.color }} />
                                                <span className="text-xs font-medium" style={{ color: status.color }}>{status.label}</span>
                                            </div>
                                        </div>

                                        {/* Items */}
                                        {order.items.map((item, i) => (
                                            <div key={i} className="flex items-center gap-4 py-3 border-t border-[#E5E7EB]">
                                                <img src={item.image} alt={item.name} className="w-14 h-18 object-cover rounded-sm" />
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-[#1A1A1A]">{item.name}</p>
                                                    <p className="text-xs text-[#6B7280]">Qty: {item.quantity}</p>
                                                </div>
                                            </div>
                                        ))}

                                        {/* Footer */}
                                        <div className="flex items-center justify-between pt-4 border-t border-[#E5E7EB]">
                                            <span className="text-sm font-semibold text-[#1B4965]">Total: {'\u20B9'}{order.total.toLocaleString()}</span>
                                            <Button variant="outline" size="sm" onClick={() => navigate(`/template4/orders/${order.id}`)}>
                                                View Details
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default Orders;
